import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, AlertTriangle, FileText, ArrowUpDown, Filter, RefreshCw } from 'lucide-react';
import { fetchInspections, deleteInspection } from '../services/inspectionService';
import { restoreSession, clearSession } from '../services/segmentService';
import SwipeableListItem from './SwipeableListItem';
import AppBarMenu from './AppBarMenu';
import InspectionDetail from './InspectionDetail';
import Logo from './Logo';
import packageJson from '../../package.json';

// Dialog component
const Dialog = ({ open, onClose, title, children, actions }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-lg shadow-lg max-w-sm w-full p-6">
        <h2 className="text-lg font-semibold text-text mb-2">{title}</h2>
        <div className="text-text-muted mb-6">{children}</div>
        <div className="flex justify-end gap-3">
          {actions}
        </div>
      </div>
    </div>
  );
};

// Alert component
const Alert = ({ severity, children, className = '' }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]} ${className}`}>
      {children}
    </div>
  );
};

const InspectionsLayout = ({ onLogout, showMessage, isAdmin = false }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Sort and filter state
  const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt' | 'vehicleId'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | status value

  // Pull-to-refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const listRef = useRef(null);
  const PULL_THRESHOLD = 80; // pixels to trigger refresh

  const { id: selectedId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Available status options for filter
  const statusOptions = ['all', 'PENDING', 'COMPLETED', 'TRANSCRIBING', 'FAILED'];

  // Normalize backend status values to display values
  const normalizeStatus = (backendStatus) => {
    if (!backendStatus) return 'PENDING';
    const upper = backendStatus.toUpperCase();
    // Map various failure statuses to FAILED
    if (upper.includes('FAILED') || upper.includes('ERROR')) {
      return 'FAILED';
    }
    // Map PROCESSING to TRANSCRIBING (same conceptual state)
    if (upper === 'PROCESSING') {
      return 'TRANSCRIBING';
    }
    return backendStatus;
  };

  // Filter and sort inspections
  const filteredAndSortedInspections = useMemo(() => {
    let result = [...inspections];

    // Apply status filter (normalize status for comparison)
    if (statusFilter !== 'all') {
      result = result.filter(i => normalizeStatus(i.status) === statusFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'createdAt') {
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === 'vehicleId') {
        comparison = (a.vehicleId || '').localeCompare(b.vehicleId || '');
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [inspections, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    loadInspections();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // Reload inspections when navigating back to this page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const loadInspections = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchInspections({ limit: 50 });
      setInspections(data);
    } catch (err) {
      console.error('Error loading inspections:', err);
      setError(err.message || t('list.error'));
    } finally {
      setLoading(false);
    }
  };

  // Pull-to-refresh: refresh without showing loading spinner
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || !isOnline) return;

    setIsRefreshing(true);
    try {
      const data = await fetchInspections({ limit: 50 });
      setInspections(data);
      setError('');
    } catch (err) {
      console.error('Error refreshing inspections:', err);
      // Don't set error on refresh - keep existing data
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [isRefreshing, isOnline]);

  // Touch handlers for pull-to-refresh
  const handleTouchStart = useCallback((e) => {
    // Only track if at top of scroll
    if (listRef.current && listRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current || isRefreshing) return;

    // Only allow pull when at top of scroll
    if (listRef.current && listRef.current.scrollTop > 0) {
      touchStartY.current = 0;
      setPullDistance(0);
      return;
    }

    const currentY = e.touches[0].clientY;
    const distance = currentY - touchStartY.current;

    if (distance > 0) {
      // Apply resistance to pull (diminishing returns)
      const resistedDistance = Math.min(distance * 0.5, PULL_THRESHOLD * 1.5);
      setPullDistance(resistedDistance);
    }
  }, [isRefreshing, PULL_THRESHOLD]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
    touchStartY.current = 0;
  }, [pullDistance, PULL_THRESHOLD, isRefreshing, handleRefresh]);

  const getStatusBadge = (status) => {
    const normalizedStatus = normalizeStatus(status);
    const styles = {
      COMPLETED: 'badge-success',
      TRANSCRIBING: 'badge-warning',
      FAILED: 'badge-error',
      UPLOADED: 'badge-info',
    };

    return (
      <span className={`badge ${styles[normalizedStatus] || 'badge-info'}`}>
        {t(`status.${normalizedStatus.toLowerCase()}`, normalizedStatus)}
      </span>
    );
  };

  const formatDate = (isoString) => {
    // Backend sends UTC timestamps without 'Z' suffix - append it if missing
    const utcString = isoString.endsWith('Z') ? isoString : isoString + 'Z';
    const date = new Date(utcString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSelectInspection = (inspection) => {
    // For PENDING inspections (not yet finalized), navigate to recording view
    // to continue adding segments
    if (inspection.status === 'PENDING') {
      navigate(`/record/${inspection.inspectionId}`);
      return;
    }

    // For other statuses, navigate to detail view
    navigate(`/inspections/${inspection.inspectionId}`);
  };

  const handleDeleteClick = (inspection) => {
    setInspectionToDelete(inspection);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setInspectionToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!inspectionToDelete) return;

    try {
      await deleteInspection(inspectionToDelete.inspectionId);
      setInspections(prev => prev.filter(i => i.inspectionId !== inspectionToDelete.inspectionId));
      showMessage?.(t('list.delete_success'), 'success');

      // If we deleted the currently selected inspection, navigate back to list
      if (selectedId === inspectionToDelete.inspectionId) {
        navigate('/');
      }
    } catch (err) {
      console.error('Error deleting inspection:', err);
      showMessage?.(err.message || t('common.error'), 'error');
    } finally {
      setDeleteDialogOpen(false);
      setInspectionToDelete(null);
    }
  };

  // Callback when detail view deletes an inspection
  const handleDetailDelete = (deletedId) => {
    setInspections(prev => prev.filter(i => i.inspectionId !== deletedId));
    navigate('/');
  };

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* App Bar */}
      <header className="app-bar flex-shrink-0">
        <Logo size={40} />
        <h1 className="flex-1 text-white font-normal text-xl">
          {t('list.title')}
        </h1>
        <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />
      </header>

      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-warning text-white px-4 py-2 flex items-center gap-2 flex-shrink-0">
          <AlertTriangle size={18} />
          <span className="text-sm">{t('list.offline_banner')}</span>
        </div>
      )}

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 lg:flex overflow-hidden">
        {/* List Panel */}
        <div className={`lg:w-96 lg:flex-shrink-0 lg:border-r lg:border-border flex flex-col h-full ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
          {/* New Inspection Button */}
          <div className="px-4 pt-4">
            <button
              onClick={() => {
                // Clear any existing session before starting a new inspection
                clearSession();
                navigate('/record');
              }}
              className="w-full bg-surface text-text border border-border hover:bg-background py-3 px-6 rounded-md font-medium cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={20} />
              <span>{t('list.new_report')}</span>
            </button>

            {/* Sort and Filter Controls */}
            <div className="flex gap-2 mt-3">
              {/* Sort Dropdown */}
              <div className="flex-1 relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="w-full appearance-none bg-surface border border-border rounded-md py-2 pl-8 pr-8 text-sm text-text cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="createdAt-desc">{t('list.sort_date_newest')}</option>
                  <option value="createdAt-asc">{t('list.sort_date_oldest')}</option>
                  <option value="vehicleId-asc">{t('list.sort_plate_az')}</option>
                  <option value="vehicleId-desc">{t('list.sort_plate_za')}</option>
                </select>
                <ArrowUpDown size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
              </div>

              {/* Filter Dropdown */}
              <div className="flex-1 relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full appearance-none bg-surface border border-border rounded-md py-2 pl-8 pr-8 text-sm text-text cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {status === 'all' ? t('list.filter_all') : t(`status.${status.toLowerCase()}`, status)}
                    </option>
                  ))}
                </select>
                <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
              </div>
            </div>
          </div>

          <main
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-4"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Pull-to-refresh indicator */}
            {(pullDistance > 0 || isRefreshing) && (
              <div
                className="flex justify-center items-center transition-all duration-200"
                style={{
                  height: isRefreshing ? 48 : pullDistance,
                  marginBottom: (pullDistance > 0 || isRefreshing) ? 8 : 0,
                  opacity: isRefreshing ? 1 : Math.min(pullDistance / PULL_THRESHOLD, 1)
                }}
              >
                <RefreshCw
                  size={24}
                  className={`text-primary ${isRefreshing ? 'animate-spin' : ''}`}
                  style={{
                    transform: isRefreshing ? 'rotate(0deg)' : `rotate(${(pullDistance / PULL_THRESHOLD) * 180}deg)`,
                    transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
                  }}
                />
              </div>
            )}

            {loading && (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            {!loading && !error && inspections.length === 0 && (
              <div className="card text-center py-8">
                <p className="text-text-muted">{t('list.empty')}</p>
              </div>
            )}

            {!loading && !error && inspections.length > 0 && filteredAndSortedInspections.length === 0 && (
              <div className="card text-center py-8">
                <p className="text-text-muted">{t('list.no_matches')}</p>
              </div>
            )}

            {!loading && !error && filteredAndSortedInspections.length > 0 && (
              <ul className="space-y-3">
                {filteredAndSortedInspections.map((inspection) => (
                  <li key={inspection.inspectionId}>
                    <div className={`card p-0 overflow-hidden transition-all ${selectedId === inspection.inspectionId ? 'bg-background' : ''}`}>
                      <SwipeableListItem
                        onDelete={() => handleDeleteClick(inspection)}
                        disabled={!isOnline}
                      >
                        <button
                          onClick={() => handleSelectInspection(inspection)}
                          className="w-full flex items-center gap-4 p-4 text-left hover:bg-background transition-colors cursor-pointer"
                        >
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-text text-base">
                              {inspection.title || inspection.vehicleId || t('list.untitled')}
                            </h3>
                            <p className="text-sm text-text-muted mt-0.5">
                              {formatDate(inspection.createdAt)}
                            </p>
                          </div>
                          <div className="flex-shrink-0 flex flex-col items-end gap-1">
                            {getStatusBadge(inspection.status)}
                            <span className="text-xs text-text-muted font-mono">
                              #{inspection.inspectionId.slice(-7)}
                            </span>
                          </div>
                        </button>
                      </SwipeableListItem>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </main>

          {/* Version Footer - Fixed at very bottom */}
          <div className="fixed bottom-0 left-0 lg:left-auto lg:w-96 right-0 lg:right-auto px-4 py-1 flex items-center justify-center gap-2 text-xs text-text-muted bg-background">
            <div
              aria-label="GTÜ"
              className="bg-text-muted"
              style={{
                height: 10,
                aspectRatio: '190 / 84',
                WebkitMaskImage: 'url(/gtue_white.svg)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                maskImage: 'url(/gtue_white.svg)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
              }}
            />
            <div
              aria-label="Slalom"
              className="bg-text-muted"
              style={{
                height: 6,
                aspectRatio: '175 / 46',
                WebkitMaskImage: 'url(/slalom_white.svg)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                maskImage: 'url(/slalom_white.svg)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
              }}
            />
            <span className="text-text-muted/40">|</span>
            Holocron {packageJson.version}
          </div>
        </div>

        {/* Detail Panel - Desktop only when no selection, always on mobile via route */}
        <div className={`lg:flex-1 overflow-y-auto ${selectedId ? 'block h-full' : 'hidden lg:flex lg:items-center lg:justify-center'}`}>
          {selectedId ? (
            <InspectionDetail
              onLogout={onLogout}
              showMessage={showMessage}
              embedded={true}
              onDelete={handleDetailDelete}
              isAdmin={isAdmin}
            />
          ) : (
            <div className="text-center text-text-muted p-8">
              <FileText size={64} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg">{t('list.select_report')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title={t('list.delete_confirm')}
        actions={
          <>
            <button
              onClick={handleDeleteCancel}
              className="btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-primary bg-error hover:bg-error-dark"
            >
              {t('common.delete')}
            </button>
          </>
        }
      >
        {inspectionToDelete && t('list.delete_message', { vehicleId: inspectionToDelete.vehicleId })}
      </Dialog>
    </div>
  );
};

export default InspectionsLayout;
